import { Browser } from "@capacitor/browser";
import { FCM } from "@capacitor-community/fcm";
import { PushNotifications } from "@capacitor/push-notifications";
import store from "../store";

class PushNotificationManager {}

/**
 * Get the channel
 * @param {object} channel
 * @returns channel name
 */
PushNotificationManager.getChannelName = (channel) => {
  var channelName = channel.id;
  if (process.env.NODE_ENV !== "production") {
    channelName = `${channelName}-${process.env.NODE_ENV}`;
  }

  return channelName;
};

/**
 * Unsubscribe to Push Notifications
 * @param {object} channel
 * @param {string} recaptchaToken
 */
PushNotificationManager.unsubscribe = async (channel, recaptchaToken) => {
  try {
    if (channel.id === "garbage_push") {
      const deviceToken = store.state.notifications.fcmToken.token;
      await store.dispatch("notifications/unsubscribeToGarbagePushNotifications", { deviceToken, recaptchaToken });
    }

    await FCM.unsubscribeFrom({ topic: PushNotificationManager.getChannelName(channel) });
    store.commit("notifications/setSubscribeToChannel", {
      channel: channel,
      status: false,
    });
  } catch (error) {
    console.error(error);
  }
};

/**
 * Subscribe to Push Notifications
 * @param {object} channel
 * @param {string} recaptchaToken
 */
PushNotificationManager.subscribe = async (channel, recaptchaToken) => {
  try {
    const permission = await PushNotifications.checkPermissions();
    if (permission.receive !== "granted") {
      const permissionRequest = await PushNotifications.requestPermissions();
      if (permissionRequest.receive !== "granted") {
        console.error("Push notification permission not granted");
        return;
      }
    }

    await PushNotifications.register();
    await store.dispatch("notifications/fetchFcmToken");

    if (channel.id === "garbage_push") {
      const deviceToken = store.state.notifications.fcmToken.token;
      const { garbageDate, garbageDay, recycleDate, recycleDay } = store.state.myvb;
      const source = "Mobile_Device";

      await store.dispatch("notifications/subscribeToGarbagePushNotifications", {
        deviceToken,
        garbageDate,
        garbageDay,
        recycleDate,
        recycleDay,
        source,
        recaptchaToken,
      });
    }

    await FCM.subscribeTo({ topic: PushNotificationManager.getChannelName(channel) });
    store.commit("notifications/setSubscribeToChannel", {
      channel: channel,
      status: true,
    });
  } catch (error) {
    console.error("Error subscribing to channel:", error);
  }
};

/**
 * Register for push notifications
 * @param {string} recaptchaToken
 *
 */
PushNotificationManager.register = async (recaptchaToken) => {
  try {
    const permission = await PushNotifications.requestPermissions();
    if (permission.receive === "granted") {
      await PushNotifications.register();

      // Fetch and store the FCM token
      await store.dispatch("notifications/fetchFcmToken");

      PushNotifications.addListener("registrationError", (error) => {
        console.error("Registration error:", error);
      });

      let channels = store.state.notifications.channels;
      for (var i = 0; i < channels.length; i++) {
        if (channels[i].subscribed == undefined) {
          await PushNotificationManager.subscribe(channels[i], recaptchaToken);
        }
      }

      PushNotifications.addListener("pushNotificationActionPerformed", async (notification) => {
        await PushNotificationManager.actionPerformed(notification);
      });
    }
  } catch (error) {
    console.error("Error Registering for Push Notifications: ", error);
  }
};

/**
 * Check push notification permissions
 * @returns {object} permission status
 */
PushNotificationManager.checkPermissions = async () => {
  const permissionStatus = await PushNotifications.checkPermissions();
  return permissionStatus;
};

/**
 * Request push notification permissions
 * @returns {object} permission status
 */
PushNotificationManager.requestPermissions = async () => {
  const permissionStatus = await PushNotifications.requestPermissions();
  return permissionStatus;
};

/**
 * Action performed when a push notification is clicked
 * @param {object} notification
 */
PushNotificationManager.actionPerformed = async (notification) => {
  if (notification.notification.data.url.length > 0) {
    let url = notification.notification.data.url;
    Browser.open({ url: url });
  }
};

export { PushNotificationManager };
